import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export function ProjectLightbox({ images, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imgElement, setImgElement] = useState(null);
  const [closeBtnStyle, setCloseBtnStyle] = useState({});

  const updateCloseBtnPosition = useCallback((imgEl) => {
    if (!imgEl) return;
    if (window.innerWidth > 1068) {
      setCloseBtnStyle({});
      return;
    }
    
    const rect = imgEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const btnSize = 48;
    const gap = 30;
    
    let topPos = rect.top - gap - btnSize;
    let rightPos = window.innerWidth - rect.right;
    
    // Fallback if not enough space above
    if (topPos < 10) {
      topPos = rect.bottom + gap;
      if (topPos + btnSize > window.innerHeight - 10) {
        topPos = window.innerHeight - btnSize - 10;
      }
    }
    
    // Ensure it stays horizontally within the viewport
    if (rightPos < 10) rightPos = 10;
    if (window.innerWidth - rightPos - btnSize < 10) {
      rightPos = window.innerWidth - btnSize - 10;
    }
    
    setCloseBtnStyle({ top: `${topPos}px`, right: `${rightPos}px` });
  }, []);

  useEffect(() => {
    if (!imgElement) return;
    
    const handleResize = () => updateCloseBtnPosition(imgElement);
    const observer = new ResizeObserver(handleResize);
    observer.observe(imgElement);
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    requestAnimationFrame(handleResize);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [imgElement, updateCloseBtnPosition]);

  // Lock body scroll
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, onClose, images.length]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleDragEnd = (e, { offset, velocity }) => {
    const swipe = offset.x;
    const swipeVelocity = velocity.x;

    if (swipe < -50 || swipeVelocity < -500) {
      handleNext();
    } else if (swipe > 50 || swipeVelocity > 500) {
      handlePrev();
    }
  };

  return (
    <motion.div
      className="lightbox-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <button 
        className="lightbox-close" 
        onClick={onClose}
        aria-label="Close lightbox"
        style={closeBtnStyle}
      >
        <X size={28} />
      </button>

      {images.length > 1 && (
        <>
          <button 
            className="lightbox-nav lightbox-nav-left" 
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            aria-label="Previous image"
          >
            <ChevronLeft size={32} />
          </button>
          
          <button 
            className="lightbox-nav lightbox-nav-right" 
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            aria-label="Next image"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}

      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            ref={setImgElement}
            onLoad={(e) => updateCloseBtnPosition(e.target)}
            src={images[currentIndex]}
            alt={`Project screenshot ${currentIndex + 1}`}
            className="lightbox-image"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.8}
            onDragEnd={handleDragEnd}
          />
        </AnimatePresence>
        
        {images.length > 1 && (
          <div className="lightbox-dots">
            {images.map((_, i) => (
              <button
                key={i}
                className={`lightbox-dot ${i === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
